/**
 * Theme store (BUILD_SPEC.md §15).
 *
 * Persists the user's choice of "light" or "dark" to localStorage and
 * mirrors it to the `dark` class on <html> so Tailwind's class-based
 * dark variants apply. We sync to the document on every state change
 * (subscribe) so any caller — store action, devtools, manual store
 * mutation — keeps the DOM in lockstep.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "light" | "dark";

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

/** Mirrors the active theme onto <html> so Tailwind's `dark:` works. */
function applyDocumentTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  // Update the meta theme-color so mobile browsers paint the URL bar
  // with the matching shade. Hex values mirror §15.2 canvas tokens.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta instanceof HTMLMetaElement) {
    meta.content = theme === "dark" ? "#0c0a09" : "#fafaf9";
  }
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      // Spec doesn't dictate a default; light is the safer choice for
      // first impression on a non-TV form factor (login from a phone).
      theme: "light",
      setTheme: (theme) => {
        set({ theme });
        applyDocumentTheme(theme);
      },
      toggle: () => {
        const next: Theme = get().theme === "dark" ? "light" : "dark";
        set({ theme: next });
        applyDocumentTheme(next);
      },
    }),
    {
      name: "azzam.theme",
      // Apply the rehydrated theme to the DOM on first load so the
      // initial paint matches the persisted choice (no flash of the
      // wrong theme).
      onRehydrateStorage: () => (state) => {
        if (state) applyDocumentTheme(state.theme);
      },
    },
  ),
);
